import { ClientProfileResponse } from '@/api/clients/get-client-profile'
import { ABILITIES } from '@/components/Can'
import { isProductionBuild } from '@/utils/environment'

import { CLIENT_PROFILE_TAB, VALID_TABS } from './client-profile.constants'
import { ClientProfileTab } from './client-profile.types'

const isModuleAvailable = (
  abilityKey: keyof typeof ABILITIES.Navbar,
  flag: boolean | undefined,
  userRole: string | null,
  isAdmin: boolean,
  isLoading: boolean,
) => {
  const roleAllowList = ABILITIES.Navbar[abilityKey] as string[]
  if (userRole && !roleAllowList.includes(userRole)) return false
  return isLoading || isAdmin || !!flag
}

export const isReservationsAvailable = (
  clientData: ClientProfileResponse | undefined,
  userRole: string | null,
  isAdmin: boolean,
  isLoading: boolean,
) =>
  isModuleAvailable(
    'reservations',
    clientData?.buildingId?.amenityReservations,
    userRole,
    isAdmin,
    isLoading,
  )

// Note: mirrors src/components/Can.js's Navbar gating exactly - each module tab requires BOTH
// (1) the resident's OWN building (not whatever building is globally selected in the top nav) to
// have the module's feed flag enabled, same as isBuildingOptionAvailable, with an Administrator
// bypass on that half only, and (2) the current user's role to be in that module's
// ABILITIES.Navbar allow-list - unlike the flag check, this has NO Administrator bypass baked in
// (Can.js doesn't give it one either), it just happens that Administrator is already in every one
// of these allow-lists. Confirmed with the team 2026-09-14: ShiftManager must not reach
// Inspections/Gift Cards at all, matching their (Admin/Manager/PropertyManager-only) allow-lists.
// Role is known synchronously (Redux), so that half applies immediately; the flag half still
// defaults to "available" while the profile is loading, to avoid flashing every tab hidden before
// clientData resolves.
export const getModuleAvailability = (
  clientData: ClientProfileResponse | undefined,
  userRole: string | null,
  isAdmin: boolean,
  isLoading: boolean,
): Partial<Record<ClientProfileTab, boolean>> => {
  const buildingFlags = clientData?.buildingId

  const isAvailable = (abilityKey: keyof typeof ABILITIES.Navbar, flag?: boolean) =>
    isModuleAvailable(abilityKey, flag, userRole, isAdmin, isLoading)

  return {
    [CLIENT_PROFILE_TAB.GiftCards]: isAvailable('rewards', buildingFlags?.rewardsAvailable),
    [CLIENT_PROFILE_TAB.Inspections]: isAvailable('checklists', buildingFlags?.checklistsAvailable),
    // Note: keylessAccessAvailable is the building's parent keyless-access toggle - no Can.js
    // Navbar entry exists for it (unlike the others below), so this skips the role-allowlist half
    // of isAvailable() and only applies the loading/admin-bypass/flag half directly. This is
    // orthogonal to which *specific* provider (Dormakaba/Salto Space/...) is actually configured -
    // see keyless-access.tsx's own empty state for that half.
    [CLIENT_PROFILE_TAB.KeylessAccess]:
      isLoading || isAdmin || !!buildingFlags?.keylessAccessAvailable,
    [CLIENT_PROFILE_TAB.Maintenance]: isAvailable(
      'maintenance',
      buildingFlags?.maintenanceRequestAvailable,
    ),
    [CLIENT_PROFILE_TAB.Packages]: isAvailable('packages', buildingFlags?.packagesAvailable),
    [CLIENT_PROFILE_TAB.Visitors]: isAvailable('visitors', buildingFlags?.guestAccess),
    // TODO: remove the production gate once the new Reservations pages launch.
    [CLIENT_PROFILE_TAB.Reservations]:
      !isProductionBuild && isReservationsAvailable(clientData, userRole, isAdmin, isLoading),
  }
}

export const resolveActiveTab = (
  rawTab: string | null,
  amenityPassesAvailable: boolean,
  isTrainer: boolean,
  moduleAvailability: Partial<Record<ClientProfileTab, boolean>>,
): ClientProfileTab => {
  if (!rawTab || !VALID_TABS.includes(rawTab as ClientProfileTab))
    return CLIENT_PROFILE_TAB.GeneralInformation
  if (
    (rawTab === CLIENT_PROFILE_TAB.Activity || rawTab === CLIENT_PROFILE_TAB.KeylessAccess) &&
    isTrainer
  )
    return CLIENT_PROFILE_TAB.GeneralInformation
  if (rawTab === CLIENT_PROFILE_TAB.Amenities && !amenityPassesAvailable)
    return CLIENT_PROFILE_TAB.GeneralInformation
  if (moduleAvailability[rawTab as ClientProfileTab] === false)
    return CLIENT_PROFILE_TAB.GeneralInformation
  return rawTab as ClientProfileTab
}

export const getUnitLabel = (apartment: number | null, buildingNumber: string | null) =>
  [apartment, buildingNumber].filter(Boolean).join(' ') || null
